"use client"

import { motion } from "framer-motion"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { ArrowLeft, RefreshCw, Clock, AlertCircle, CheckCircle } from "lucide-react"
import { Link } from "react-router-dom"
import Footer from "@/components/Common/Footer"
import ERPLeadBot from "./ERPLeadBot";

export default function RefundPolicy() {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  }

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <motion.div
            className="flex items-center gap-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/" className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Home</span>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <motion.div
          className="max-w-4xl mx-auto"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Header Section */}
          <motion.div className="text-center mb-12" variants={fadeIn}>
            <Badge className="mb-4 bg-blue-100 text-blue-800 hover:bg-blue-200">
              <RefreshCw className="h-4 w-4 mr-2" />
              REFUND POLICY
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Refund Policy
            </h1>
            <p className="text-xl text-gray-600">
              Our commitment to customer satisfaction and fair refund practices
            </p>
          </motion.div>

          {/* Policy Content */}
          <motion.div variants={fadeIn}>
            <Card className="shadow-lg border-0">
              <CardContent className="p-8">
                <div className="prose prose-lg max-w-none">
                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      <Clock className="h-6 w-6 mr-2 text-blue-600" />
                      Overview
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      At Clynox Solutions, we are committed to providing high-quality school management software solutions. We understand that sometimes a service may not meet your expectations, and we want to ensure you have a clear understanding of our refund policy.
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      This refund policy outlines the terms and conditions under which refunds may be provided for our services. By using our platform and services, you agree to the terms outlined in this policy.
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Eligibility for Refunds</h2>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-gray-700 font-medium">Service Not Delivered</p>
                          <p className="text-gray-600 text-sm">If we fail to deliver the promised service within the agreed timeframe</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-gray-700 font-medium">Technical Issues</p>
                          <p className="text-gray-600 text-sm">If the service is unusable due to technical problems on our end</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-gray-700 font-medium">Service Cancellation</p>
                          <p className="text-gray-600 text-sm">If you cancel the service within the specified cancellation period</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Refund Process</h2>
                    <div className="bg-blue-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-blue-800 mb-4">Step-by-Step Process:</h3>
                      <div className="space-y-3">
                        <div className="flex items-start">
                          <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</div>
                          <p className="text-blue-800">Submit a refund request through our customer support</p>
                        </div>
                        <div className="flex items-start">
                          <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</div>
                          <p className="text-blue-800">Provide necessary documentation and reason for refund</p>
                        </div>
                        <div className="flex items-start">
                          <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</div>
                          <p className="text-blue-800">Our team will review your request within 5-7 business days</p>
                        </div>
                        <div className="flex items-start">
                          <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</div>
                          <p className="text-blue-800">The refund will be credited back to the original payment method within 10-15 business days</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Refund Timeline</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Review Period</h3>
                        <p className="text-gray-600">5-7 business days</p>
                        <p className="text-sm text-gray-500 mt-2">Time taken to review your refund request</p>
                      </div>
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Processing Time</h3>
                        <p className="text-gray-600">10-15 business days</p>
                        <p className="text-sm text-gray-500 mt-2">Time taken to process approved refunds</p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Non-Refundable Items</h2>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-gray-700 font-medium">Custom Development</p>
                          <p className="text-gray-600 text-sm">Custom features or modifications specifically developed for your institution</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-gray-700 font-medium">Data Migration Services</p>
                          <p className="text-gray-600 text-sm">One-time data migration and setup services</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-gray-700 font-medium">Training Services</p>
                          <p className="text-gray-600 text-sm">Staff training and onboarding services</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-gray-700 font-medium">Consultation Fees</p>
                          <p className="text-gray-600 text-sm">Professional consultation and advisory services</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Partial Refunds</h2>
                    <p className="text-gray-700 leading-relaxed">
                      In certain circumstances, we may offer partial refunds based on the following criteria:
                    </p>
                    <ul className="list-disc list-inside space-y-2 mt-4 text-gray-700">
                      <li>Proportional refund based on unused service period</li>
                      <li>Refund minus costs incurred for custom work already completed</li>
                      <li>Refund minus administrative and processing fees</li>
                      <li>Refund minus costs for any third-party services already procured</li>
                    </ul>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Refund Methods</h2>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <h3 className="font-semibold text-green-800 mb-2">Original Payment Method</h3>
                        <p className="text-green-700 text-sm">Refunds will be processed to the original payment method used for the transaction</p>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <h3 className="font-semibold text-blue-800 mb-2">Bank Transfer</h3>
                        <p className="text-blue-700 text-sm">For certain cases, refunds may be processed via bank transfer</p>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                        <h3 className="font-semibold text-purple-800 mb-2">Credit Note</h3>
                        <p className="text-purple-700 text-sm">In some cases, a credit note may be issued for future services</p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <p className="text-gray-700 mb-4">
                        For refund requests or questions about this policy, please contact us:
                      </p>
                      <div className="space-y-2">
                        <p className="text-gray-700"><strong>Email:</strong> clynoxofficial@gmail.com</p>
                        <p className="text-gray-700"><strong>Phone:</strong> +91-7499070944</p>
                        <p className="text-gray-700"><strong>Business Hours:</strong> Monday - Friday (9:00 AM - 6:00 PM IST)</p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Policy Updates</h2>
                    <p className="text-gray-700 leading-relaxed">
                      We reserve the right to modify this refund policy at any time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically to stay informed about our refund practices.
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </motion.div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
      <Footer />
      <ERPLeadBot />
    </div>
  )
} 